/**
 * Garage Collection View
 * Shows user's favorited tools
 */

import React, { useState, useEffect } from 'react';
import { Heart, ExternalLink, Trash2, Sparkles } from 'lucide-react';
import garageAPI from '../../../services/garage/api';
import GarageLoadingScreen from '../components/GarageLoadingScreen';
import TourGuide from '../components/TourGuide';
import { COLLECTION_TOUR_STEPS } from '../tours/collectionTour';

const GarageCollectionView = ({ userEmail, onOpenTool, showOnboarding = false, onboardingChecked = true }) => {
  const [favorites, setFavorites] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);

  // Tour state — start after onboarding so they never overlap
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    if (!onboardingChecked || showOnboarding) {
      setShowTour(false);
      return;
    }
    const tourDone = localStorage.getItem('tour-completed-collection') === 'true';
    setShowTour(!tourDone);
  }, [onboardingChecked, showOnboarding]);

  useEffect(() => {
    loadFavorites();
  }, [userEmail]);

  const loadFavorites = async () => {
    try {
      setLoading(true);
      const favIds = await garageAPI.getUserFavorites(userEmail);
      setFavorites(favIds);
      
      // Load tool details
      const allTools = await garageAPI.getTools({ status: 'published' });
      const favoriteTools = allTools.filter(tool => favIds.includes(tool.id));
      setTools(favoriteTools);
    } catch (error) {
      console.error('Failed to load favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (toolId) => {
    try {
      await garageAPI.removeFavorite(toolId, userEmail);
      setFavorites(favorites.filter(id => id !== toolId));
      setTools(tools.filter(tool => tool.id !== toolId));
    } catch (error) {
      console.error('Failed to remove favorite:', error);
    }
  };

  const handleLaunchTool = async (tool) => {
    try {
      await garageAPI.logUsage(tool.id, userEmail);
      window.open(tool.url, '_blank');
    } catch (error) {
      console.error('Failed to log usage:', error);
      window.open(tool.url, '_blank');
    }
  };

  return (
    <div className="garage-content">
      <div className="garage-page-hero">
        <div>
          <h1 className="garage-page-title">My collection</h1>
          <p className="garage-page-subtitle">Your favorited tools for quick access</p>
        </div>
        <div className="garage-hero-actions">
          <button
            className="garage-btn garage-btn-secondary"
            onClick={() => setShowTour(true)}
            title="Take a quick tour"
          >
            <Sparkles size={16} />
            Help
          </button>
        </div>
      </div>

      <div className="garage-tools-section">
        {loading ? (
          <GarageLoadingScreen message="Loading favorites..." />
        ) : tools.length === 0 ? (
          <div className="garage-empty">
            <div className="garage-empty-icon">⭐</div>
            <h3>No favorites yet</h3>
            <p>Start favoriting tools to build your collection</p>
          </div>
        ) : (
          <div className="garage-tools-grid" data-tour="collection-tools">
            {tools.map(tool => (
              <div key={tool.id} className="garage-tool-card" onClick={() => onOpenTool(tool.id)}>
                {tool.imageUrl ? (
                  <div className={`garage-tool-thumbnail ${tool.toolType === 'Gem' ? 'is-gem' : 'is-app'}`}>
                    <img src={tool.imageUrl} alt={tool.title} />
                  </div>
                ) : (
                  <div className={`garage-tool-thumbnail garage-tool-thumbnail-placeholder ${tool.toolType === 'Gem' ? 'is-gem' : 'is-app'}`}>
                    <div className="garage-tool-placeholder-icon">{tool.toolType === 'Gem' ? '✨' : '📱'}</div>
                  </div>
                )}

                <button
                  className="garage-tool-favorite favorited"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFavorite(tool.id);
                  }}
                >
                  <Trash2 size={16} />
                </button>

                <div className="garage-tool-content">
                  <h3 className="garage-tool-title">{tool.title}</h3>
                  <p className="garage-tool-description">{tool.description}</p>

                  {tool.createdBy && (
                    <p className="garage-tool-creator">Created by: <strong>{tool.createdBy}</strong></p>
                  )}

                  <div className="garage-tool-actions">
                    <button 
                      className="garage-tool-btn garage-tool-btn-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenTool(tool.id);
                      }}
                    >
                      Details
                    </button>
                    <button 
                      className="garage-tool-btn garage-tool-btn-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLaunchTool(tool);
                      }}
                    >
                      Launch
                      <ExternalLink size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Helper Tour — only after onboarding is done */}
      {showTour && !showOnboarding && (
        <TourGuide
          steps={COLLECTION_TOUR_STEPS}
          storageKey="tour-completed-collection"
          onComplete={() => setShowTour(false)}
          onSkip={() => setShowTour(false)}
        />
      )}
    </div>
  );
};

export default GarageCollectionView;
